package org.gymly.i18n

import org.gymly.model.AdminNavGroup
import org.gymly.model.AdminPersonSex
import org.gymly.model.AdminRoute
import org.gymly.model.AdminWeekday

object StringsEs : GymlyStrings {
    override val locale = GymlyLocale.ES
    override val common = object : CommonStrings {
        override val save = "Guardar"
        override val saveChanges = "Guardar cambios"
        override val saving = "Guardando…"
        override val cancel = "Cancelar"
        override val delete = "Eliminar"
        override val remove = "Quitar"
        override val edit = "Editar"
        override val add = "Agregar"
        override val create = "Crear"
        override val close = "Cerrar"
        override val search = "Buscar"
        override val clear = "Limpiar"
        override val retry = "Reintentar"
        override val loading = "Cargando…"
        override val noResults = "Sin resultados"
        override val confirm = "Confirmar"
        override val yes = "Sí"
        override val no = "No"
        override val all = "Todos"
        override val select = "Seleccionar"
        override val active = "Activo"
        override val inactive = "Inactivo"
        override val actions = "Acciones"
        override val status = "Estado"
        override val total = "Total"
        override val date = "Fecha"
        override val amount = "Monto"
        override val name = "Nombre"
        override val lastName = "Apellido"
        override val email = "Correo"
        override val phone = "Teléfono"
        override val address = "Dirección"
        override val document = "Documento / CI"
        override val sex = "Sexo"
        override val notes = "Notas"
        override val expand = "Expandir"
        override val collapse = "Contraer"
        override val previous = "Anterior"
        override val next = "Siguiente"
        override val emptyValue = "—"
        override val genericError = "Ocurrió un error."
        override val noRecords = "Sin registros."
        override val listLoadFailed = "No se pudo cargar la lista."
        override fun sexLabel(value: AdminPersonSex) = when (value) {
            AdminPersonSex.MALE -> "Masculino"
            AdminPersonSex.FEMALE -> "Femenino"
            AdminPersonSex.OTHER -> "Otro"
        }

        override fun weekdayShort(day: AdminWeekday) = when (day) {
            AdminWeekday.MONDAY -> "Lun"
            AdminWeekday.TUESDAY -> "Mar"
            AdminWeekday.WEDNESDAY -> "Mié"
            AdminWeekday.THURSDAY -> "Jue"
            AdminWeekday.FRIDAY -> "Vie"
            AdminWeekday.SATURDAY -> "Sáb"
            AdminWeekday.SUNDAY -> "Dom"
        }

        override fun weekdayLong(day: AdminWeekday) = when (day) {
            AdminWeekday.MONDAY -> "Lunes"
            AdminWeekday.TUESDAY -> "Martes"
            AdminWeekday.WEDNESDAY -> "Miércoles"
            AdminWeekday.THURSDAY -> "Jueves"
            AdminWeekday.FRIDAY -> "Viernes"
            AdminWeekday.SATURDAY -> "Sábado"
            AdminWeekday.SUNDAY -> "Domingo"
        }

        override fun monthShort(month: Int) = MONTHS_ES.getOrElse(month) { "" }
    }

    override val nav = object : NavStrings {
        override fun route(route: AdminRoute) = when (route) {
            AdminRoute.Dashboard -> "Panel"
            AdminRoute.BulkSignup -> "Alta masiva"
            AdminRoute.Clients -> "Clientes"
            AdminRoute.Workers -> "Trabajadores"
            AdminRoute.Services -> "Servicios"
            AdminRoute.MembershipPlans -> "Planes de membresía"
            AdminRoute.MembershipOrders -> "Órdenes de membresía"
            AdminRoute.Memberships -> "Membresías"
            AdminRoute.ClientAccounts -> "Monedero digital"
            AdminRoute.Orders -> "Órdenes"
            AdminRoute.Payments -> "Pagos"
            AdminRoute.Marketing -> "Marketing"
            AdminRoute.Products -> "Productos"
            AdminRoute.StoreSales -> "Ventas"
            AdminRoute.Schedules -> "Horarios semanales"
            AdminRoute.Bookings -> "Asistencia y sesiones"
            AdminRoute.Profile -> "Perfil"
            AdminRoute.Reports -> "Reportes"
            AdminRoute.Settings -> "Configuración"
            AdminRoute.TermsMonitoring -> "Monitoreo de términos"
        }

        override fun group(group: AdminNavGroup) = when (group) {
            AdminNavGroup.People -> "Personal"
            AdminNavGroup.Sales -> "Catálogo y ventas"
            AdminNavGroup.Store -> "Tienda"
            AdminNavGroup.Agenda -> "Horario y reservas"
            AdminNavGroup.System -> "Sistema"
        }
    }

    override val shell = object : ShellStrings {
        override val searchPlaceholder = "Buscar en toda la app..."
        override val notifications = "Notificaciones"
        override val menu = "Menú"
        override val logout = "Salir"
        override fun searchType(type: String) = when (type) {
            "CLIENT" -> "Cliente"
            "STAFF" -> "Trabajador"
            "MEMBERSHIP" -> "Membresía"
            "PLAN" -> "Plan"
            "PAYMENT" -> "Pago"
            "BOOKING" -> "Reserva"
            "PRODUCT" -> "Producto"
            else -> type
        }
    }

    override val profile = object : ProfileStrings {
        override val title = "Perfil"
        override val subtitle = "Actualiza tus datos de trabajador, foto y contraseña."
        override val personalData = "Datos personales"
        override val workData = "Datos laborales"
        override val firstNameRequired = "Nombre *"
        override val lastNameRequired = "Apellido *"
        override val firstNamePlaceholder = "Nombre"
        override val lastNamePlaceholder = "Apellido"
        override val emailPlaceholder = "correo@ejemplo.com"
        override val phonePlaceholder = "Ej. +58 412 0000000"
        override val documentPlaceholder = "Cédula o pasaporte"
        override val addressRequired = "Dirección *"
        override val addressPlaceholder = "Calle, número, ciudad"
        override val sexRequired = "Sexo *"
        override val language = "Idioma"
        override val languageSavedHint = "Se guarda al elegir el idioma."
        override val spanish = "Español"
        override val english = "English"
        override val role = "Rol"
        override val branch = "Sucursal"
        override val workSchedule = "Jornada laboral"
        override val workScheduleEmpty = "Sin jornada definida"
        override val workScheduleOffDay = "No trabaja"
        override val joinedAt = "Alta"
        override val changePassword = "Cambiar contraseña"
        override val changePasswordHint = "Déjalo vacío si no quieres cambiarla."
        override val currentPassword = "Contraseña actual"
        override val newPassword = "Nueva contraseña"
        override val confirmNewPassword = "Confirmar nueva"
        override val newPasswordPlaceholder = "Mínimo 8 caracteres"
        override val confirmPasswordPlaceholder = "Repite la nueva contraseña"
        override val profilePhoto = "Foto de perfil"
        override val uploadPhoto = "Subir foto"
        override val changePhoto = "Cambiar foto"
        override val removePhoto = "Eliminar foto"
        override val photoNotSaved = "La foto no quedó guardada en el servidor. Prueba Guardar cambios."
        override val photoNotSavedRetry = "La foto no quedó guardada. Reintenta."
        override val photoSaveHint = "Pulsa Guardar cambios si no se subió sola."
        override val noImageSelected = "No se seleccionó ninguna imagen."
        override val photoUpdated = "Foto actualizada. Sincronizada con el terminal de acceso."
        override val photoUploadFailed = "No se pudo subir la foto. Prueba Guardar cambios."
        override val photoDeleted = "Foto eliminada."
        override val photoDeleteFailed = "No se pudo eliminar la foto."
        override val profileLoadFailed = "No se pudo cargar el perfil."
        override val profileSaveFailed = "No se pudo guardar el perfil."
        override val nameRequiredError = "Nombre y apellido son obligatorios."
        override val addressRequiredError = "La dirección es obligatoria."
        override val sexRequiredError = "El sexo es obligatorio."
        override val needCurrentPassword = "Indica tu contraseña actual."
        override val passwordTooShort = "La nueva contraseña debe tener al menos 8 caracteres."
        override val passwordsDoNotMatch = "Las contraseñas no coinciden."
        override val savedProfile = "Perfil guardado."
        override val savedProfileAndPassword = "Perfil y contraseña actualizados."
        override val savedPhotoAndPassword = "Foto y contraseña actualizados."
        override val savedPhotoAndProfile = "Foto subida y perfil guardado."
        override fun selectedFile(name: String) = "Seleccionada: $name"
    }

    override val dashboard = object : DashboardStrings {
        override val loadFailed = "No se pudo cargar el panel."
        override val quickActions = "Acciones rápidas"
        override val quickActionsHint = "Atajos de recepción — lo del día a día."
        override val registerSale = "Registrar venta"
        override val registerSaleHint = "Tienda · POP y suplementos"
        override val membershipOrder = "Orden de membresía"
        override val membershipOrderHint = "Inscribir y cobrar"
        override val bulkEntry = "Alta masiva"
        override val bulkEntryHint = "Inscribir o cobrar en lote"
        override val assignMembership = "Asignar membresía"
        override val assignMembershipHint = "Socio + plan + entrenador"
        override val membershipPlans = "Planes de membresía"
        override val membershipPlansHint = "Catálogo de planes"
        override val attendanceSessions = "Asistencia y sesiones"
        override val attendanceSessionsHint = "Piso, reservas y personal"
        override val activity = "Actividad"
        override val bookingsTodayWeek = "Reservas hoy / semana"
        override val paymentsOnTime = "Pagos a tiempo"
        override val activeClients = "clientes activos"
        override val trainers = "entrenadores"
        override val scheduleSlots = "franjas"
        override val closeDoor = "Cerrar puerta"
        override val closeDoorHint = "Si no cerró sola a los 10 s"
        override val doorClosed = "Puerta cerrada."
        override val doorCloseFailed = "No se pudo cerrar la puerta."
        override val accessTitle = "Acceso · terminal"
        override val accessHint = "Entradas del Hikvision y simulaciones."
        override val accessEmpty = "Aún no hay eventos. Configura el terminal en Configuración → Acceso."
        override val unknownPerson = "Desconocido"
        override val trainerSchedule = "Horario del entrenador"
        override val noSlotsToShow = "Sin franjas para mostrar."
        override val allTrainers = "Todos los entrenadores"
        override val allTrainersHint = "Filtra la grilla del panel eligiendo un entrenador abajo."
        override val noTrainers = "No hay usuarios con rol TRAINER."
        override val addTrainer = "+ AGREGAR ENTRENADOR"
        override val calendarTitle = "Calendario · reservas"
        override val noBookingsInPeriod = "Sin reservas en el periodo."
        override fun bookingsDetail(today: Long, week: Long) =
            "$today de $week confirmadas esta semana"

        override fun paymentsDetail(percent: Int) = "$percent% (PAID vs PENDING)"
        override fun weekBookingsSummary(count: Int) =
            "Lunes a viernes · $count reserva(s) esta semana"

        override fun bookedOfCapacity(booked: Int, capacity: Int) = "$booked/$capacity res."
        override fun panelFilter(trainerCount: Int) = "Filtro del panel · $trainerCount entrenadores"
    }
}

private val MONTHS_ES = listOf(
    "", "ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic",
)
